'use client';

import { forwardRef, useImperativeHandle, useState } from 'react';

import {
  Checkbox,
  CheckboxGroup,
  type CheckboxGroupProps,
  Heading,
  Spacer,
  Stack,
  Text,
  Skeleton,
} from '@backyard-ui/core';

import { type Optional } from '@/infra/services/catalog/service/model';
import { useCrossesServices } from '@/presenters/hooks/catalog/use-crosses-services';
import { formatPrice } from '@/utils/price/formatPrice';

import useOptionalsAnalytics from './useOptionalsAnalytics';

export interface OptionalsProps {
  itemId: number;
  onChange: (optionals: Optional[]) => void;
}

export interface OptionalsRef {
  clearAll: () => void;
}

const Optionals = forwardRef<OptionalsRef, OptionalsProps>((props, ref) => {
  const { itemId, onChange } = props;
  const [selectedOptionals, setSelectedOptionals] = useState<string[]>([]);

  useImperativeHandle(ref, () => ({
    clearAll: () => {
      setSelectedOptionals([]);
      onChange([]);
    },
  }));

  const { data: optionals, isFetching } = useCrossesServices(itemId, {
    enabled: false,
  });

  useOptionalsAnalytics(optionals?.data);

  if (isFetching) {
    return <Skeleton height="72px" data-testid="optionals-skeleton" />;
  }

  if (!optionals || optionals.data.length === 0) return null;

  const handleCheckboxGroupChange: CheckboxGroupProps['onChange'] = (value) => {
    const selectedOptionals = optionals.data.filter((optional) =>
      value.includes(String(optional.id))
    );
    onChange(selectedOptionals);

    setSelectedOptionals(value);
  };

  return (
    <Stack direction="column" spacing={2}>
      <Heading size="xs" color="green-600" asChild>
        <h2>Opcionais</h2>
      </Heading>
      <Spacer size="2" axis="vertical" />
      <Stack direction="column" spacing={6}>
        <CheckboxGroup
          value={selectedOptionals}
          onChange={handleCheckboxGroupChange}
        >
          {optionals.data.map((optional) => (
            <Checkbox size="sm" key={optional.id} value={String(optional.id)}>
              <Text size="md" weight="normal">
                {`${optional.name} (+ ${formatPrice(optional.price.to)})`}
              </Text>
            </Checkbox>
          ))}
        </CheckboxGroup>
      </Stack>
    </Stack>
  );
});

Optionals.displayName = 'Optionals';

export default Optionals;
